import { useState } from 'react';
import Helmet from 'react-helmet';
import { styled } from '@mui/material/styles';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  GlobalStyles,
  Grid, InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import '../../App.css';

import axios from 'axios';
import { getApiBaseUrl } from '../../utils/env';
import PublicLayout from './PublicLayout';
import PropTypes from 'prop-types';
import {
  ContentWrap,
  GridCenterAligned as GridCenterAlignedBase,
  GridContentItem
} from '../common/GridLayout';
import { Headline as BaseHeadline } from '../common/PublicTypography';
import { shadeBgStyles } from '../../utils/styles';
import { ReactComponent as ArrowIcon } from '../../icons/button-arrow-white.svg';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const GridMobileWithBackground = styled(Grid)`
    position: relative;

    &:before {
        ${props => props.theme.breakpoints.down('md')} {
            content: '';
            background-image: url('/bg-home.png');
            height: 100%;
            position: absolute;
            top: 0;
            width: calc(100vw + 30px);
            left: -30px;
            transform: scaleY(-1);
            background-size: 100% 88%;
            background-repeat: no-repeat;
        }
    }
`;

const Headline = styled(BaseHeadline)`
    font-size: 16px;
    line-height: 1.14;
    max-width: 280px;
    margin-top: 0;

    ${props => props.theme.breakpoints.up('md')} {
        font-size: 32px;
        max-width: none;
    }
`;

const LoginShowcaseImage = styled('div')`
    width: 100%;
    aspect-ratio: 1 / 0.75;
    position: relative;
    overflow: hidden;

    ${props => props.theme.breakpoints.up('md')} {
        overflow: visible;
        flex: 1;
    }

    &:after {
        content: '';
        background-image: linear-gradient(to bottom, rgb(235, 241, 255, 0), rgb(235, 241, 255, 0.4)),
        url(/mission-control-final-2.svg);
        height: 100%;
        position: absolute;
        top: 10px;
        width: 100%;
        background-size: 100%;
        background-position: top left;
        background-repeat: no-repeat no-repeat;
        z-index: 0;

        ${props => props.theme.breakpoints.up('md')} {
            background-image: url(/mission-control-final-2.svg);
            top: 20px;
            height: calc(100% + 130px);
            background-size: contain;
            width: 200%;
            background-position: top left 10px;
            background-repeat: no-repeat;
        }
    }
`;

const Form = styled('form')`
    background-color: #ebf1ee;
    padding: 30px;
    border-radius: 4px;
    margin-bottom: 20px;

    ${props => props.theme.breakpoints.up('md')} {
        margin-top: 20px;
        margin-right: 40px;
    }
`;

const ErrorMessage = styled(Typography)`
    color: #d32f2f;
    margin: 16px 0;
`;

const SuccessMessage = styled(Typography)`
    color: darkgreen;
    margin: 16px 0;
`;

const FormCtaRoot = styled(Box)`
    position: relative;
    padding-top: 30px;
`;

const GridCenterAligned = styled(GridCenterAlignedBase)`
    ${props => props.theme.breakpoints.up('md')} {
        gap: 70px;
    }
`;

function CreatePassword({ className, title }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ password: '', repeatPassword: '' });
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?/])[A-Za-z\d!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?/]{8,}$/;

  const validate = () => {
    const newErrors = { password: '', repeatPassword: '' };
    if (!PASSWORD_REGEX.test(password)) {
      newErrors.password = 'Password is too short / weak.';
    }
    setErrors(newErrors);
    return !newErrors.password && !newErrors.repeatPassword;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (busy) return;

    if (validate()) {
      setBusy(true);
      setServerError('');
      setSuccessMessage('');
      const searchParams = new URLSearchParams(window.location.search);

      await axios.post(`${getApiBaseUrl()}/auth/create-password`, {
        password: password,
        token: searchParams.get('token')
      }).then(response => {
        setBusy(false);
        setSuccessMessage('Password has been set for your account. You can now log in with your new password.');
        setPassword('');
        setErrors({ password: '', repeatPassword: '' });
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }).catch(error => {
        setBusy(false);
        setServerError(typeof error === 'string' ? error : 'Sorry, something went wrong. Please try again later.');
      });
    }

  };

  const pageBackgroundStyles = {
    body: {
      backgroundColor: '#274bde',
      backgroundImage: 'url("/v2/pixels-bg.svg")',
      backgroundPosition: 'top left',
      backgroundRepeat: 'repeat-x',
    },
  };

  const handleToggleVisibility = () => {
    setShowPassword((show) => !show);
  };

  return (
    <PublicLayout className={className}>
      <GlobalStyles styles={{ ...shadeBgStyles, ...pageBackgroundStyles }}/>
      <Helmet title="Pixels - Log Into Your Account">
        <meta
          name="description"
          content="Welcome to Pixels! Log into our centralised portal where you can oversee video matches, exercise control over selections, and obtain insights for your instream video. Our intuitive and user-friendly platform is designed to streamline your editorial workflow."
        />
      </Helmet>
      <GridCenterAligned container>
        <GridContentItem item xs={12} md={6}>
          <ContentWrap className={'ctn-root'} id="ContentWrap">
            <Headline variant={'h1'}>{title}</Headline>
            <Typography variant={'body1'}>Password must contain at least 8 characters, one uppercase
              letter, one lowercase letter, one number and one special character.</Typography>

            <Form onSubmit={handleSubmit}>
              <Grid container rowSpacing={2} spacing={3}>
                {serverError && (<Grid item xs={12}>
                  <ErrorMessage>{serverError}</ErrorMessage>
                </Grid>)}
                {successMessage && (<Grid item xs={12}><SuccessMessage>{successMessage}</SuccessMessage></Grid>)}
                <Grid item md={12} xs={12}>
                  <FormControl margin="dense" fullWidth>
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      autoComplete="new-password"
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleToggleVisibility} edge="end">
                              {showPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      required
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <FormCtaRoot>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                  <Grid item xs={12}>
                    <Button
                      type={'submit'}
                      style={{
                        fontSize: 17,
                        fontWeight: 700,
                        minHeight: '50px',
                        backgroundColor: '#3355D1',
                        textTransform: 'none',
                        justifyContent: 'space-between',
                      }}
                      fullWidth
                      variant="contained"
                      startIcon={<></>}
                      endIcon={busy ? <CircularProgress size={24} color="inherit"/> : <ArrowIcon/>}
                    >
                      {title}
                    </Button>
                  </Grid>
                </Grid>
              </FormCtaRoot>
            </Form>
          </ContentWrap>
        </GridContentItem>
        <Grid
          item
          xs={12}
          md={6}
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('md')]: {
              display: 'none',
            },
          })}
          id="LoginShowcaseImageWrapper"
        >
        </Grid>
      </GridCenterAligned>
      <GridMobileWithBackground
        sx={theme => ({
          [theme.breakpoints.up('md')]: {
            display: 'none',
          },
        })}
      >
        <LoginShowcaseImage/>
      </GridMobileWithBackground>
    </PublicLayout>
  );
}

CreatePassword.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
};

export default styled(CreatePassword)`
    .content-root {
        display: flex;
    }

    .content-inner {
        max-width: none;
        padding: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        > div:first-of-type {
            flex: 1;
        }
    }

    ${props => props.theme.breakpoints.up('md')} {
        .ctn-root {
            padding-top: 20px;
        }
    }
`;
